import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";
import type { APIResponse, Connection } from "@/lib/types";

export const connectionOwnerPaths: Record<string, string> = {
  incident: "incidents",
  vehicles: "vehicles",
  insurances: "insurances",
  incidentReports: "incident-reports",
  expenses: "expenses",
  liens: "liens",
  litigationDates: "litigation-dates",
  lostWages: "lost-wages",
  propertyDamages: "property-damages",
  settlementDemands: "settlement-demands",
  settlementOffers: "settlement-offers",
  settlementCounters: "settlement-counters",
  statutesOfLimitations: "statute-of-limitations",
  trustChecks: "trust-checks",
  medicalTreatments: "medical-treatments",
  medicalTreatmentAppointments: "medical-treatments/appointments",
  appointments: "medical-treatments/appointments",
  medicalTreatmentBills: "medical-treatments/bills",
  bills: "medical-treatments/bills",
  medicalTreatmentRecords: "medical-treatments/records",
  records: "medical-treatments/records",
  medicalTreatmentPlans: "medical-treatments/plans",
  plans: "medical-treatments/plans",
  medicalTreatmentBalanceConfirmations:
    "medical-treatments/balance-confirmations",
  balanceConfirmations: "medical-treatments/balance-confirmations",
  signatureRequests: "signature-requests",
  shareFolders: "share-folders",
  surveys: "surveys",
};

type OwnerPage = {
  connections: Connection[];
  connectionsNextCursor?: string | null;
};
export type ConnectionFilters = {
  connectionType?: string;
  asOf?: string;
  history?: boolean;
  connectionLimit?: number;
};
export const ownedConnectionQueryKey = (
  path: string,
  id: string,
  matterId: string,
  filters: ConnectionFilters = {},
  cursor?: string,
) =>
  [
    "v4",
    "object-connections",
    path,
    id,
    { matterId, ...filters, connectionCursor: cursor },
  ] as const;

/** Complete before exposing selectors/counts or issuing set-based role changes.
 * Continuations always use the owner endpoint and retain the same snapshot.
 */
export async function completeOwnedConnections<T extends OwnerPage>(
  owner: T,
  path: string,
  matterId: string,
  filters: ConnectionFilters = {},
): Promise<T> {
  const connections = [...owner.connections];
  let cursor = owner.connectionsNextCursor;
  const visited = new Set<string>();
  while (cursor) {
    if (visited.has(cursor))
      throw new Error("The connection page did not advance.");
    visited.add(cursor);
    const response = await authenticatedFastApiAxiosInstance.get<
      APIResponse<OwnerPage>
    >(path, {
      params: {
        matterId,
        connectionLimit: 25,
        history: false,
        ...filters,
        connectionCursor: cursor,
      },
    });
    connections.push(...response.data.data.connections);
    cursor = response.data.data.connectionsNextCursor;
  }
  // Identity is assignment ID, never Contact ID: overlapping contributions survive.
  return {
    ...owner,
    connections: [
      ...new Map(
        connections.map((connection) => [connection.id, connection]),
      ).values(),
    ],
    connectionsNextCursor: null,
  };
}

export async function completeMatterConnections<T>(
  matter: T,
  matterId: string,
): Promise<T> {
  const visit = async (value: unknown, family?: string): Promise<unknown> => {
    if (Array.isArray(value)) {
      const result = [];
      for (const item of value) result.push(await visit(item, family));
      return result;
    }
    if (!value || typeof value !== "object") return value;
    let record = value as Record<string, unknown>;
    const ownerId = record.id ?? record.matterSignatureRequestId;
    if (Array.isArray(record.connections) && record.connectionsNextCursor) {
      const path =
        ownerId === matterId
          ? `matters/v4/${encodeURIComponent(matterId)}`
          : family &&
              connectionOwnerPaths[family] &&
              typeof ownerId === "string"
            ? `matters/v4/${connectionOwnerPaths[family]}/${encodeURIComponent(ownerId)}`
            : undefined;
      if (!path)
        throw new Error("Missing endpoint for an incomplete connection owner.");
      record = await completeOwnedConnections(
        record as OwnerPage & Record<string, unknown>,
        path,
        matterId,
      );
    }
    const result = { ...record };
    for (const [key, child] of Object.entries(record)) {
      if (connectionOwnerPaths[key] && child && typeof child === "object")
        result[key] = await visit(child, key);
    }
    return result;
  };
  return (await visit(matter)) as T;
}
